import { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  UploadCloud,
  Loader2,
  Sparkles,
  CornerDownLeft,
} from "lucide-react";
import { WHATSAPP_LINK } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const hasReview = (a, name) => (a.proof_options || []).includes(name);

const STEPS = [
  { id: "welcome", type: "welcome" },
  {
    id: "s1",
    type: "intro",
    num: "01",
    title: "Project Scope & Goals",
    sub: "Six quick sections. One question at a time. About 4 minutes of your time for a website that pays for itself.",
  },
  {
    id: "existing_website_url",
    type: "text",
    q: "Do you have an existing website?",
    help: "If this is a redesign, paste the URL. If not, hit Skip.",
    placeholder: "https://yourcurrentsite.com",
    optional: true,
  },
  {
    id: "domain_owned",
    type: "yesno",
    q: "Do you currently own a domain name?",
  },
  {
    id: "domain_details",
    type: "text",
    q: "What's the domain — and where is it registered?",
    placeholder: "yourbusiness.com · GoDaddy, Namecheap…",
    showIf: (a) => a.domain_owned === "Yes",
  },
  {
    id: "primary_objective",
    type: "choice",
    q: "What's the #1 job your new website must do?",
    options: [
      "Inbound phone calls",
      "Quote form submissions",
      "Appointment bookings",
      "Direct online checkout",
    ],
    otherKey: "primary_objective_other",
    required: true,
  },
  {
    id: "primary_cta",
    type: "text",
    q: "What should the main button say?",
    help: 'e.g. "Request a Quote", "Book an Inspection", "Call Today"',
    placeholder: "Request a Quote",
  },
  {
    id: "local_competitors",
    type: "group",
    q: "Name 1–2 local competitors you're up against.",
    help: "Local rivals reveal the keywords they target, typical packages in your market, and the local design standard we need to beat.",
    optional: true,
    fields: [
      { key: "local_url_1", label: "Competitor URL 1", placeholder: "https://…" },
      { key: "local_url_2", label: "Competitor URL 2", placeholder: "https://…", optional: true },
      {
        key: "local_notes",
        label: "What do they do well — or what will you do better?",
        type: "textarea",
        optional: true,
      },
    ],
  },
  {
    id: "national_leaders",
    type: "group",
    q: "Now — 1–2 regional or national leaders you admire.",
    help: "Bigger brands have bigger design budgets. We borrow their proven user flows, feature layouts and modern aesthetics.",
    optional: true,
    fields: [
      { key: "national_url_1", label: "Industry leader URL 1", placeholder: "https://…" },
      { key: "national_url_2", label: "Industry leader URL 2", placeholder: "https://…", optional: true },
      {
        key: "national_admire",
        label: "What do you admire? (layout, modern vibe, interactive tools, clear pricing)",
        type: "textarea",
        optional: true,
      },
    ],
  },
  {
    id: "design_inspiration",
    type: "textarea",
    q: "Any websites outside your industry whose look & feel you love?",
    placeholder: "https://… — love the dark theme and the motion",
    optional: true,
  },
  { id: "s2", type: "intro", num: "02", title: "Business Identity & Branding" },
  {
    id: "business_name",
    type: "text",
    q: "Official business name",
    required: true,
    placeholder: "Acme Services LLC",
  },
  {
    id: "tagline",
    type: "text",
    q: "Tagline or one-line hook",
    optional: true,
    placeholder: '"We ignite growth."',
  },
  {
    id: "brand_colors",
    type: "text",
    q: "Primary brand colors",
    help: "Names or hex codes — whatever you have.",
    optional: true,
    placeholder: "Navy & gold · #2B426F #D9A92E",
  },
  {
    id: "aesthetic_vibe",
    type: "choice",
    q: "Pick the vibe that feels like you.",
    options: ["Modern & Clean", "Bold & Industrial", "High-End Luxury", "Warm & Friendly"],
    otherKey: "aesthetic_vibe_other",
  },
  {
    id: "has_logo",
    type: "yesno",
    q: "Do you have an existing high-resolution logo?",
  },
  {
    id: "logo_file",
    type: "file",
    q: "Upload your logo",
    help: "Vector, PNG, SVG or PDF — up to 10MB.",
    accept: ".png,.jpg,.jpeg,.webp,.svg,.pdf",
    showIf: (a) => a.has_logo === "Yes",
  },
  {
    id: "logo_brief",
    type: "group",
    q: "No logo? No problem — we'll design one.",
    showIf: (a) => a.has_logo === "No",
    fields: [
      { key: "logo_business_name", label: "Exact business name for the logo" },
      { key: "logo_tagline", label: "Tagline to include (if any)", optional: true },
    ],
  },
  {
    id: "logo_style",
    type: "choice",
    q: "Which logo style speaks to you?",
    options: ["Wordmark", "Icon + Text", "Emblem", "Not sure — advise me"],
    showIf: (a) => a.has_logo === "No",
  },
  { id: "s3", type: "intro", num: "03", title: "About You & Your Value" },
  {
    id: "about_headline",
    type: "text",
    q: "Headline for your About section",
    placeholder: '"About Acme" or "Reliable Service Backed by Experience"',
    optional: true,
  },
  {
    id: "bio",
    type: "textarea",
    q: "Your story — 1–2 short paragraphs.",
    help: "Founder story, why you started, what drives you.",
    optional: true,
  },
  {
    id: "milestones",
    type: "text",
    q: "Credibility markers",
    placeholder: '"Serving South Florida since 2018" · "1,000+ projects completed"',
    optional: true,
  },
  {
    id: "differentiators",
    type: "textarea",
    q: "3–4 reasons clients pick you over competitors.",
    optional: true,
  },
  {
    id: "about_photo",
    type: "file",
    q: "A photo for your About section",
    help: "Founder, crew, workshop or vehicle fleet — up to 10MB.",
    accept: "image/*",
    optional: true,
  },
  { id: "s4", type: "intro", num: "04", title: "Your Services" },
  {
    id: "service_1",
    type: "group",
    q: "Service #1 — your headline act.",
    fields: [
      { key: "service_1_title", label: "Service title", required: true },
      { key: "service_1_desc", label: "Short description (2–3 sentences)", type: "textarea" },
      { key: "service_1_image", label: "Icon or photo (optional)", type: "file", optional: true },
    ],
  },
  {
    id: "service_2",
    type: "group",
    q: "Service #2",
    optional: true,
    fields: [
      { key: "service_2_title", label: "Service title" },
      { key: "service_2_desc", label: "Short description", type: "textarea", optional: true },
      { key: "service_2_image", label: "Icon or photo (optional)", type: "file", optional: true },
    ],
  },
  {
    id: "service_3",
    type: "group",
    q: "Service #3",
    optional: true,
    fields: [
      { key: "service_3_title", label: "Service title" },
      { key: "service_3_desc", label: "Short description", type: "textarea", optional: true },
      { key: "service_3_image", label: "Icon or photo (optional)", type: "file", optional: true },
    ],
  },
  {
    id: "service_4",
    type: "group",
    q: "Service #4 — last one.",
    optional: true,
    fields: [
      { key: "service_4_title", label: "Service title" },
      { key: "service_4_desc", label: "Short description", type: "textarea", optional: true },
      { key: "service_4_image", label: "Icon or photo (optional)", type: "file", optional: true },
    ],
  },
  { id: "s5", type: "intro", num: "05", title: "Proof & Process" },
  {
    id: "proof_options",
    type: "multichoice",
    q: "What should we include? Choose all that apply.",
    options: ["How It Works (3 steps)", "Portfolio / Recent Work", "Client Reviews"],
    optional: true,
  },
  {
    id: "how_it_works",
    type: "group",
    q: "Your 3 simple steps.",
    showIf: (a) => hasReview(a, "How It Works (3 steps)"),
    fields: [
      { key: "step_1", label: "Step 1 — Contact / Consultation" },
      { key: "step_2", label: "Step 2 — Execution / Build / Production" },
      { key: "step_3", label: "Step 3 — Delivery / Final Result" },
    ],
  },
  {
    id: "portfolio",
    type: "group",
    q: "Show off your recent work.",
    showIf: (a) => hasReview(a, "Portfolio / Recent Work"),
    fields: [
      {
        key: "portfolio_link",
        label: "Cloud folder link (Google Drive / Dropbox)",
        placeholder: "https://drive.google.com/…",
        optional: true,
      },
      {
        key: "portfolio_photos",
        label: "Or upload 6–10 high-res photos",
        type: "file",
        multiple: true,
        optional: true,
      },
    ],
  },
  {
    id: "review_1",
    type: "group",
    q: "Client review #1",
    showIf: (a) => hasReview(a, "Client Reviews"),
    fields: [
      { key: "review_1_quote", label: "The review", type: "textarea" },
      { key: "review_1_name", label: "Client name" },
      { key: "review_1_meta", label: "Project type / city", optional: true },
    ],
  },
  {
    id: "review_2",
    type: "group",
    q: "Client review #2",
    optional: true,
    showIf: (a) => hasReview(a, "Client Reviews"),
    fields: [
      { key: "review_2_quote", label: "The review", type: "textarea" },
      { key: "review_2_name", label: "Client name" },
      { key: "review_2_meta", label: "Project type / city", optional: true },
    ],
  },
  {
    id: "review_3",
    type: "group",
    q: "Client review #3",
    optional: true,
    showIf: (a) => hasReview(a, "Client Reviews"),
    fields: [
      { key: "review_3_quote", label: "The review", type: "textarea" },
      { key: "review_3_name", label: "Client name" },
      { key: "review_3_meta", label: "Project type / city", optional: true },
    ],
  },
  { id: "s6", type: "intro", num: "06", title: "Contact & Integrations" },
  {
    id: "contact_form_fields",
    type: "multichoice",
    q: "What should your website's contact form ask for?",
    options: ["Name", "Email", "Phone", "Project Address", "File Upload"],
  },
  {
    id: "lead_emails",
    type: "text",
    q: "Where should new leads be emailed?",
    required: true,
    placeholder: "you@business.com, sales@business.com",
  },
  {
    id: "integrations",
    type: "multichoice",
    q: "Any integrations to wire in?",
    options: ["Google Analytics", "CRM Webhook", "Calendly", "WhatsApp Button", "None / Not sure"],
    optional: true,
  },
  {
    id: "contact_details",
    type: "group",
    q: "Your public contact details.",
    fields: [
      { key: "phone", label: "Direct phone number", required: true },
      { key: "business_email", label: "Direct business email", type: "email", required: true },
      { key: "address_area", label: "Physical address / service area coverage", optional: true },
      { key: "hours", label: "Hours of operation", optional: true },
    ],
  },
  {
    id: "social_links",
    type: "textarea",
    q: "Social media profile links",
    placeholder: "Instagram, Facebook, LinkedIn, TikTok…",
    optional: true,
  },
  {
    id: "your_details",
    type: "group",
    q: "Last thing — who do we talk to about this project?",
    fields: [
      { key: "contact_name", label: "Your name", required: true },
      { key: "contact_email", label: "Your email", type: "email", required: true },
    ],
  },
  { id: "review", type: "review" },
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const inputCls =
  "w-full bg-transparent border-b-2 border-white/15 py-3 text-xl text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#D9A92E] sm:text-2xl";

const FilePicker = ({ id, files, onChange, accept, multiple }) => (
  <div data-testid={`brief-file-${id}`}>
    <label
      className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center transition-colors duration-300 hover:border-[#D9A92E]/60 hover:bg-white/[0.04]"
    >
      <UploadCloud className="h-8 w-8 text-[#D9A92E]" />
      <span className="text-sm text-zinc-400">
        {multiple ? "Drop files here or click to browse" : "Drop a file here or click to browse"}
      </span>
      <span className="mono text-[11px] uppercase tracking-widest text-zinc-600">
        Images or PDF · max 10MB each
      </span>
      <input
        type="file"
        className="hidden"
        accept={accept || "image/*,.pdf,.svg"}
        multiple={multiple}
        onChange={(e) => {
          const list = Array.from(e.target.files || []);
          if (!list.length) return;
          const big = list.find((f) => f.size > 10 * 1024 * 1024);
          if (big) {
            toast.error(`"${big.name}" is over 10MB`);
            return;
          }
          onChange(multiple ? [...(files || []), ...list] : list[0]);
          e.target.value = "";
        }}
      />
    </label>
    {files && (Array.isArray(files) ? files.length > 0 : true) && (
      <div className="mt-4 flex flex-wrap gap-2">
        {(Array.isArray(files) ? files : [files]).map((f, i) => (
          <span
            key={`${f.name}-${i}`}
            className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300"
          >
            {f.name}
            <button
              type="button"
              aria-label={`Remove ${f.name}`}
              data-testid={`brief-file-remove-${id}-${i}`}
              onClick={() =>
                onChange(
                  Array.isArray(files) ? files.filter((_, j) => j !== i) : null
                )
              }
              className="text-zinc-500 transition-colors hover:text-red-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
    )}
  </div>
);

const WebsiteBrief = () => {
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const visible = useMemo(
    () => STEPS.filter((s) => !s.showIf || s.showIf(answers)),
    [answers]
  );
  const step = visible[Math.min(idx, visible.length - 1)];
  const progress = Math.round((idx / Math.max(visible.length - 1, 1)) * 100);

  const setValue = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));
  const setFile = (key, value) => setFiles((f) => ({ ...f, [key]: value }));

  const validate = useCallback(() => {
    if (!step) return true;
    const checkField = (f) => {
      const v = (answers[f.key] || "").trim();
      if (f.required && !v) {
        toast.error(`"${f.label}" is required`);
        return false;
      }
      if (v && f.type === "email" && !EMAIL_RE.test(v)) {
        toast.error(`"${f.label}" needs a valid email address`);
        return false;
      }
      return true;
    };
    if (step.type === "text" || step.type === "textarea") {
      const v = (answers[step.id] || "").trim();
      if (step.required && !v) {
        toast.error("This one is required — it helps us get it right.");
        return false;
      }
      return true;
    }
    if (step.type === "choice") {
      const v = answers[step.id];
      if (step.required && !v) {
        toast.error("Pick the closest option — you can refine later.");
        return false;
      }
      if (v === "__other" && !(answers[step.otherKey] || "").trim()) {
        toast.error("Tell us a little more in the text field.");
        return false;
      }
      return true;
    }
    if (step.type === "multichoice") {
      if (step.required && !(answers[step.id] || []).length) {
        toast.error("Pick at least one — or choose 'None / Not sure'.");
        return false;
      }
      return true;
    }
    if (step.type === "group") {
      for (const f of step.fields) {
        if (f.type === "file") continue;
        const stepOptional = step.optional && !(answers[step.fields[0].key] || "").trim();
        if (stepOptional) continue;
        if (!checkField(f)) return false;
      }
      return true;
    }
    return true;
  }, [step, answers]);

  const next = useCallback(() => {
    if (!validate()) return;
    setDir(1);
    setIdx((i) => Math.min(i + 1, visible.length - 1));
  }, [validate, visible.length]);

  const back = useCallback(() => {
    setDir(-1);
    setIdx((i) => Math.max(i - 1, 0));
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || done || submitting) return;
      const tag = e.target.tagName;
      if (tag === "TEXTAREA" && !(e.metaKey || e.ctrlKey)) return;
      if (tag === "BUTTON") return;
      e.preventDefault();
      if (step.type === "review") return;
      next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, step, done, submitting]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify(answers));
      Object.entries(files).forEach(([key, val]) => {
        if (Array.isArray(val)) val.forEach((f) => fd.append(key, f));
        else if (val) fd.append(key, val);
      });
      await axios.post(`${API}/website-briefs`, fd);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong sending your brief. Please try again or reach us on WhatsApp."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const answeredCount = useMemo(() => {
    let n = 0;
    STEPS.forEach((s) => {
      if (s.showIf && !s.showIf(answers)) return;
      if (s.type === "text" || s.type === "textarea" || s.type === "yesno" || s.type === "choice" || s.type === "multichoice") {
        const v = answers[s.id];
        if (v && (!Array.isArray(v) || v.length)) n += 1;
      } else if (s.type === "file") {
        if (files[s.id]) n += 1;
      } else if (s.type === "group") {
        if (s.fields.some((f) => (answers[f.key] || "").trim() || files[f.key])) n += 1;
      }
    });
    return n;
  }, [answers, files]);

  const renderField = (f) => {
    if (f.type === "textarea") {
      return (
        <textarea
          rows={2}
          value={answers[f.key] || ""}
          onChange={(e) => setValue(f.key, e.target.value)}
          placeholder={f.placeholder || "Type your answer…"}
          className={`${inputCls} resize-none`}
          data-testid={`brief-input-${f.key}`}
        />
      );
    }
    if (f.type === "file") {
      return (
        <FilePicker
          id={f.key}
          files={files[f.key]}
          onChange={(v) => setFile(f.key, v)}
          multiple={f.multiple}
        />
      );
    }
    return (
      <input
        type={f.type === "email" ? "email" : "text"}
        value={answers[f.key] || ""}
        onChange={(e) => setValue(f.key, e.target.value)}
        placeholder={f.placeholder || "Type your answer…"}
        className={inputCls}
        data-testid={`brief-input-${f.key}`}
      />
    );
  };

  const renderStep = () => {
    switch (step.type) {
      case "welcome":
        return (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-2xl border border-[#D9A92E]/30 bg-[#D9A92E]/10"
            >
              <Sparkles className="h-7 w-7 text-[#E9C766]" />
            </motion.div>
            <p className="mono mb-4 text-xs uppercase tracking-[0.3em] text-[#E9C766]">
              Website Development Brief
            </p>
            <h1 className="font-display text-4xl font-bold leading-tight text-white sm:text-5xl">
              Let's build something
              <br />
              worth clicking.
            </h1>
            <p className="mx-auto mt-6 max-w-md text-base text-zinc-400">
              Answer a few quick questions — one at a time — and we'll come back
              with a plan, a look, and a price. No jargon, no commitment.
            </p>
            <button
              onClick={next}
              data-testid="brief-start-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#D9A92E] px-8 py-4 text-sm font-semibold text-[#09090B] shadow-[0_0_30px_rgba(217,169,46,0.4)] transition-all duration-300 hover:bg-[#E8C05A] hover:shadow-[0_0_45px_rgba(217,169,46,0.6)]"
            >
              Start the brief <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mono mt-5 text-[11px] uppercase tracking-widest text-zinc-600">
              Takes ~4 minutes
            </p>
          </div>
        );
      case "intro":
        return (
          <div>
            <p className="mono mb-4 text-xs uppercase tracking-[0.3em] text-[#E9C766]">
              Section {step.num}
            </p>
            <h2 className="font-display text-4xl font-bold text-white sm:text-5xl">
              {step.title}
            </h2>
            {step.sub && <p className="mt-5 max-w-lg text-base text-zinc-400">{step.sub}</p>}
            <ContinueRow onNext={next} />
          </div>
        );
      case "text":
      case "textarea":
        return (
          <div>
            <Question step={step} answers={answers} />
            {step.type === "text" ? (
              <input
                autoFocus
                type="text"
                value={answers[step.id] || ""}
                onChange={(e) => setValue(step.id, e.target.value)}
                placeholder={step.placeholder || "Type your answer…"}
                className={inputCls}
                data-testid={`brief-input-${step.id}`}
              />
            ) : (
              <textarea
                autoFocus
                rows={4}
                value={answers[step.id] || ""}
                onChange={(e) => setValue(step.id, e.target.value)}
                placeholder={step.placeholder || "Type your answer…"}
                className={`${inputCls} resize-none !text-lg`}
                data-testid={`brief-input-${step.id}`}
              />
            )}
            <ContinueRow onNext={next} optional={step.optional} onSkip={next} />
          </div>
        );
      case "yesno":
        return (
          <div>
            <Question step={step} answers={answers} />
            <div className="flex flex-col gap-3 sm:flex-row">
              {["Yes", "No"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    setValue(step.id, opt);
                    setDir(1);
                    setTimeout(() => setIdx((i) => Math.min(i + 1, visible.length - 1)), 220);
                  }}
                  data-testid={`brief-yesno-${step.id}-${opt.toLowerCase()}`}
                  className={`flex-1 rounded-2xl border px-6 py-5 text-lg font-semibold transition-all duration-200 ${
                    answers[step.id] === opt
                      ? "border-[#D9A92E] bg-[#D9A92E]/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-[#D9A92E]/50 hover:text-white"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
            <ContinueRow onNext={next} hideContinue={!!answers[step.id]} />
          </div>
        );
      case "choice": {
        const options = step.otherKey ? [...step.options, "Something else"] : step.options;
        return (
          <div>
            <Question step={step} answers={answers} />
            <div className="flex flex-col gap-3">
              {options.map((opt, i) => {
                const val = opt === "Something else" ? "__other" : opt;
                const selected = answers[step.id] === val;
                return (
                  <button
                    key={opt}
                    onClick={() => {
                      setValue(step.id, val);
                      if (val !== "__other") {
                        setDir(1);
                        setTimeout(() => setIdx((i2) => Math.min(i2 + 1, visible.length - 1)), 220);
                      }
                    }}
                    data-testid={`brief-choice-${step.id}-${i}`}
                    className={`flex items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all duration-200 ${
                      selected
                        ? "border-[#D9A92E] bg-[#D9A92E]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-[#D9A92E]/50"
                    }`}
                  >
                    <span
                      className={`mono flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-xs ${
                        selected
                          ? "border-[#D9A92E] bg-[#D9A92E] text-[#09090B]"
                          : "border-white/20 text-zinc-400"
                      }`}
                    >
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className={`text-base ${selected ? "text-white" : "text-zinc-300"}`}>
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>
            {answers[step.id] === "__other" && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
                <input
                  autoFocus
                  type="text"
                  value={answers[step.otherKey] || ""}
                  onChange={(e) => setValue(step.otherKey, e.target.value)}
                  placeholder="Tell us in your own words…"
                  className={inputCls}
                  data-testid={`brief-input-${step.otherKey}`}
                />
              </motion.div>
            )}
            <ContinueRow onNext={next} hideContinue={answers[step.id] && answers[step.id] !== "__other"} />
          </div>
        );
      }
      case "multichoice": {
        const selected = answers[step.id] || [];
        return (
          <div>
            <Question step={step} answers={answers} hint="Choose all that apply" />
            <div className="flex flex-col gap-3">
              {step.options.map((opt, i) => {
                const on = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    onClick={() =>
                      setValue(
                        step.id,
                        on ? selected.filter((o) => o !== opt) : [...selected, opt]
                      )
                    }
                    data-testid={`brief-multi-${step.id}-${i}`}
                    className={`flex items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all duration-200 ${
                      on
                        ? "border-[#D9A92E] bg-[#D9A92E]/10"
                        : "border-white/10 bg-white/[0.02] hover:border-[#D9A92E]/50"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${
                        on ? "border-[#D9A92E] bg-[#D9A92E] text-[#09090B]" : "border-white/20"
                      }`}
                    >
                      {on && <Check className="h-4 w-4" />}
                    </span>
                    <span className={`text-base ${on ? "text-white" : "text-zinc-300"}`}>{opt}</span>
                  </button>
                );
              })}
            </div>
            <ContinueRow onNext={next} optional={step.optional} onSkip={next} />
          </div>
        );
      }
      case "file":
        return (
          <div>
            <Question step={step} answers={answers} />
            <FilePicker
              id={step.id}
              files={files[step.id]}
              onChange={(v) => setFile(step.id, v)}
              accept={step.accept}
              multiple={step.multiple}
            />
            <ContinueRow onNext={next} optional={step.optional} onSkip={next} />
          </div>
        );
      case "group":
        return (
          <div>
            <Question step={step} answers={answers} />
            <div className="space-y-7">
              {step.fields.map((f) => (
                <div key={f.key}>
                  <label className="mb-2 block text-sm font-medium text-zinc-400">
                    {f.label}
                    {f.required && <span className="ml-1 text-[#D9A92E]">*</span>}
                  </label>
                  {renderField(f)}
                </div>
              ))}
            </div>
            <ContinueRow onNext={next} optional={step.optional} onSkip={next} />
          </div>
        );
      case "review":
        return (
          <div>
            <p className="mono mb-4 text-xs uppercase tracking-[0.3em] text-[#E9C766]">
              Final check
            </p>
            <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
              Ready to ignite?
            </h2>
            <p className="mt-4 max-w-lg text-base text-zinc-400">
              You've answered <span className="font-semibold text-white">{answeredCount}</span>{" "}
              questions
              {Object.keys(files).filter((k) => files[k]).length > 0 && (
                <>
                  {" "}and attached{" "}
                  <span className="font-semibold text-white">
                    {Object.values(files).reduce(
                      (n, v) => n + (Array.isArray(v) ? v.length : v ? 1 : 0),
                      0
                    )}
                  </span>{" "}
                  file(s)
                </>
              )}
              . Hit submit and we'll get to work — expect a reply within one business day.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="brief-submit-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#D9A92E] px-8 py-4 text-sm font-semibold text-[#09090B] shadow-[0_0_30px_rgba(217,169,46,0.4)] transition-all duration-300 hover:bg-[#E8C05A] hover:shadow-[0_0_45px_rgba(217,169,46,0.6)] disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {submitting ? "Sending your brief…" : "Submit my brief"}
              </button>
              <button
                onClick={back}
                data-testid="brief-review-back-button"
                className="text-sm text-zinc-400 underline-offset-4 transition-colors hover:text-white hover:underline"
              >
                Go back and edit
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (done) {
    return (
      <Shell progress={100}>
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
          data-testid="brief-success"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
            className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-[#D9A92E] shadow-[0_0_60px_rgba(217,169,46,0.5)]"
          >
            <Check className="h-9 w-9 text-[#09090B]" />
          </motion.div>
          <h1 className="font-display text-4xl font-bold text-white sm:text-5xl">
            Brief received.
          </h1>
          <p className="mx-auto mt-5 max-w-md text-base text-zinc-400">
            Our team is already reading it. Expect a reply at{" "}
            <span className="text-white">{answers.contact_email || "your email"}</span> within one
            business day.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/"
              data-testid="brief-success-home-link"
              className="rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white transition-colors hover:border-[#D9A92E]/60 hover:text-[#E9C766]"
            >
              Back to sparkplug.one
            </Link>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="brief-success-whatsapp-link"
              className="rounded-full bg-[#D9A92E] px-6 py-3 text-sm font-semibold text-[#09090B] transition-colors hover:bg-[#E8C05A]"
            >
              Chat on WhatsApp now
            </a>
          </div>
        </motion.div>
      </Shell>
    );
  }

  return (
    <Shell
      progress={progress}
      counter={`${String(Math.min(idx + 1, visible.length)).padStart(2, "0")} / ${String(visible.length).padStart(2, "0")}`}
      onBack={idx > 0 ? back : null}
      onNext={step.type !== "welcome" && step.type !== "review" ? next : null}
    >
      <AnimatePresence mode="wait" custom={dir}>
        <motion.div
          key={step.id}
          custom={dir}
          initial={{ opacity: 0, y: dir > 0 ? 40 : -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: dir > 0 ? -40 : 40 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-2xl"
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>
    </Shell>
  );
};

const Question = ({ step, hint }) => (
  <div className="mb-8">
    <p className="mono mb-3 text-xs uppercase tracking-[0.3em] text-[#E9C766]">
      {hint || (step.optional ? "Optional" : "")}
    </p>
    <h2 className="font-display text-2xl font-bold leading-snug text-white sm:text-3xl">
      {step.q}
    </h2>
    {step.help && <p className="mt-3 max-w-lg text-sm text-zinc-500">{step.help}</p>}
  </div>
);

const ContinueRow = ({ onNext, optional, onSkip, hideContinue }) => (
  <div className="mt-8 flex items-center gap-5">
    {!hideContinue && (
      <button
        onClick={onNext}
        data-testid="brief-continue-button"
        className="inline-flex items-center gap-2 rounded-full bg-[#D9A92E] px-6 py-3 text-sm font-semibold text-[#09090B] transition-all duration-300 hover:bg-[#E8C05A]"
      >
        Continue <ArrowRight className="h-4 w-4" />
      </button>
    )}
    {optional && (
      <button
        onClick={onSkip}
        data-testid="brief-skip-button"
        className="text-sm text-zinc-500 transition-colors hover:text-white"
      >
        Skip
      </button>
    )}
    <span className="mono hidden items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-600 sm:flex">
      press <CornerDownLeft className="h-3 w-3" /> Enter
    </span>
  </div>
);

const Shell = ({ children, progress, counter, onBack, onNext }) => (
  <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#09090B]">
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div className="absolute -left-40 -top-40 h-[480px] w-[480px] rounded-full bg-gradient-to-tr from-cyan-500/10 to-blue-600/10 blur-[120px]" />
      <div className="absolute -bottom-48 -right-40 h-[520px] w-[520px] rounded-full bg-gradient-to-tr from-[#D9A92E]/10 to-orange-600/10 blur-[120px]" />
    </div>
    <div className="relative z-10 flex items-center justify-between px-5 py-4 sm:px-10">
      <Link to="/" data-testid="brief-logo-link">
        <img src="/assets/sparkplug-logo.webp" alt="Spark Plug" className="h-8 w-auto" />
      </Link>
      {counter && (
        <span className="mono text-xs tracking-[0.25em] text-zinc-500" data-testid="brief-counter">
          {counter}
        </span>
      )}
      <Link
        to="/"
        aria-label="Close"
        data-testid="brief-close-button"
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:border-white/30 hover:text-white"
      >
        <X className="h-4 w-4" />
      </Link>
    </div>
    <div className="relative z-10 h-[3px] w-full bg-white/5">
      <motion.div
        className="h-full bg-gradient-to-r from-[#D9A92E] to-[#E9C766]"
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        data-testid="brief-progress-bar"
      />
    </div>
    <div className="relative z-10 flex flex-1 items-center justify-center px-5 py-14 sm:px-10">
      {children}
    </div>
    {(onBack || onNext) && (
      <div className="fixed bottom-6 right-6 z-20 flex gap-2">
        {onBack && (
          <button
            onClick={onBack}
            aria-label="Previous question"
            data-testid="brief-back-button"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#131316]/90 text-zinc-300 backdrop-blur transition-colors hover:border-[#D9A92E]/60 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        {onNext && (
          <button
            onClick={onNext}
            aria-label="Next question"
            data-testid="brief-next-button"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-[#131316]/90 text-zinc-300 backdrop-blur transition-colors hover:border-[#D9A92E]/60 hover:text-white"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    )}
  </div>
);

export default WebsiteBrief;
